import React, { useState } from "react";
import { X, Plus, PackagePlus, CircleX } from "lucide-react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import axios from "axios";
import { API_BACKEND_URL } from "../../../Config/Config";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { COLLECTION_NAME_MAX_LENGTH } from "../../../constants/collection";

const CollectionForm = ({ open, handleCloseDialog }) => {
  const queryClient = useQueryClient();
  const notify = useNotification();
  const [formData, setFormData] = useState({
    name: "",
    baseurl: "",
    description: "",
  });

  const getUserDetails = () => {
    const userDetails = localStorage.getItem("userDetail");
    if (!userDetails) throw new Error("No user details found in localStorage");
    return JSON.parse(userDetails);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.baseurl || !formData.description) {
      notify("Please fill in all required fields", "warning");
      return;
    }
    const user = getUserDetails();

    const url = `${API_BACKEND_URL}/collections?companyId=${user.companyid}`;

    try {
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      notify("Collection created successfully!", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
      queryClient.refetchQueries(["latestAPIResults"]);
      setFormData({
        name: "",
        baseurl: "",
        description: "",
      });
      handleCloseDialog();
    } catch (error) {
      const backendMessage =
        typeof error.response?.data === "string" ? error.response.data : null;
      notify(backendMessage || "Failed to create collection!", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="font-apple text-lg text-gray-800 flex items-center">
          <p>Create New Collection</p>
          <PackagePlus className="ml-2 " />
        </span>

        <button
          onClick={handleCloseDialog}
          className="flex items-center justify-center p-1.5 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer"
        >
          <CircleX size={20} className="text-red-500" />
        </button>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: "20px",
          backgroundColor: "#ffffff",
        }}
      >
        <form onSubmit={handleSubmit} className="font-apple">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Collection Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={COLLECTION_NAME_MAX_LENGTH}
                  className="w-full px-3 py-2 border border-gray-200  focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="Enter collection name"
                  required
                />
                <span className="text-xs text-gray-400 mt-1 block text-right">
                  {formData.name.length}/{COLLECTION_NAME_MAX_LENGTH}
                </span>
              </div>
              <div>
                <label className="block font-medium text-sm text-gray-700 mb-2">
                  Base URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="baseurl"
                  value={formData.baseurl}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200  focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="https://api.example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200  focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm resize-none"
                placeholder="Enter collection description"
                required
              />
            </div>
          </div>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
        }}
      >
        <button
          type="button"
          onClick={() => {
            handleCloseDialog();
            setFormData({
              name: "",
              baseurl: "",
              description: "",
            });
          }}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className="flex items-center px-4 py-2 bg-blue-600 text-white  hover:bg-blue-700 transition-all duration-200 text-sm cursor-pointer"
        >
          <Plus size={16} className="mr-2" />
          Create Collection
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionForm;
